import { useShedConfig } from "../../contexts/ShedConfigContext";
import InfoTooltip from "../layout/InfoTooltip";

const fields = [
  {
    id: "width" as const,
    label: "Width (ft)",
    tooltip: "Specify the total width of the shed.",
    min: 4,
    max: 30,
    placeholder: "8",
    type: "number",
  },
  {
    id: "length" as const,
    label: "Length (ft)",
    tooltip: "Enter the shed's length from entrance to back.",
    min: 4,
    max: 30,
    placeholder: "10",
    type: "number",
  },
  {
    id: "wallHeight" as const,
    label: "Wall height (ft)",
    tooltip: "Vertical height of side walls (doesn't include roof).",
    min: 4,
    max: 30,
    placeholder: "6",
    type: "number",
  },
  {
    id: "totalHeight" as const,
    label: "Total height (ft)",
    tooltip: "From ground to highest point of roof.",
    min: 4,
    max: 30,
    placeholder: "8",
    type: "number",
  },
  {
    id: "fenceDistance" as const,
    label: "Distance from fence (ft)",
    tooltip:
      "Space you'll leave between shed and nearest fence. This helps comply with margins required by HOA or local regulations.",
    min: 0,
    max: 50,
    placeholder: "5",
    type: "number",
  },
  {
    id: "wallExteriorColor" as const,
    label: "Wall exterior color",
    tooltip:
      "Choose the exterior color of the wall siding. This field will determine the visual finish of the shed and must comply with HOA regulations or local codes, if applicable.",
    placeholder: "Beige, White, etc.",
    type: "text",
  },
];

export default function DimensionsForm() {
  const { dimensions, hasBlocks, setDimension, setHasBlocks } = useShedConfig();

  return (
    <div className="my-2 max-w-fit">
      <h3 className="text-center font-medium text-lg mb-6">Shed dimensions</h3>

      <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:justify-center">
        {fields.map((field) => (
          <label key={field.id} className="inline-block">
            <InfoTooltip>{field.tooltip}</InfoTooltip>
            {field.label}:
            <input
              type={field.type}
              value={dimensions[field.id] || ""}
              onChange={(e) =>
                setDimension(
                  field.id,
                  field.type === "number"
                    ? parseFloat(e.target.value) || ""
                    : e.target.value,
                )
              }
              min={field.min}
              max={field.max}
              placeholder={field.placeholder}
              className="border border-gray-300 rounded px-2 py-1 ml-1 w-full md:w-auto"
            />
          </label>
        ))}
      </div>

      <div className="mt-6">
        <div className="flex align-middle justify-center mr-10 mb-3">
          <label className="content-center text-md mr-2">
            <InfoTooltip>
              Select 'Yes' if the shed will be installed on concrete blocks as a
              base, or 'No' if it will be installed directly on concrete or
              anchored to the floor.
            </InfoTooltip>
            Blocks:
          </label>
          <p className="content-center ml-2">
            Yes
            <input
              type="radio"
              name="blocks"
              checked={hasBlocks === true}
              onChange={() => setHasBlocks(true)}
              className="mr-3 ml-1"
            />
            No
            <input
              type="radio"
              name="blocks"
              checked={hasBlocks === false}
              onChange={() => setHasBlocks(false)}
              className="mr-3 ml-1"
            />
          </p>
        </div>

        <div className="flex align-middle justify-center mr-10 mb-3">
          <label className="inline-block">
            <InfoTooltip>
              Height the shed will have above ground (whether by blocks, elevated
              base or platform).
            </InfoTooltip>
            Distance from ground to shed (in):
            <input
              type="number"
              value={dimensions.floorDistance || ""}
              onChange={(e) =>
                setDimension("floorDistance", parseFloat(e.target.value) || "")
              }
              min={0}
              max={5}
              step={0.1}
              placeholder="4"
              className="w-full md:w-auto border border-gray-300 rounded px-2 py-1 ml-1"
            />
          </label>
        </div>
      </div>
    </div>
  );
}
